import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ElementRef, Injectable } from '@angular/core';
import { Logger } from '@core/services/logger.service';
import { StaticResourceService } from '@core/services/static-resource.service';
import { PopupPanelComponent } from '@shared/components/popup-panel/popup-panel.component';
import { Subscription, timer } from 'rxjs';

@Injectable({
	providedIn: 'root'
})
export class InfoPopupService {

	private readonly popupDelay = 750; // delay to display the info panel after mouse event; msec

	constructor (
		private _staticResourceService: StaticResourceService,
		private _overlayPositionBuilder: OverlayPositionBuilder,
		private _overlay: Overlay,
		private _logger: Logger
	) { }

	private readonly COMPONENT_NAME = "InfoPopupService";
	private timerSubs = new Map<string, Subscription>(); // keeps the subscriptions for started timers per packageId

	private _overlayRef: OverlayRef | undefined = undefined;
	private _mouseEnteredPopupPanel: boolean = false;

	private _cachePackageInfo = new Map<string, string | undefined>();

	private _mouseEntersSub: Subscription | undefined = undefined;
	private _mouseLeavesSub: Subscription | undefined = undefined;

	notifyMouseEntersPackageLabel(packageId: string, targetElement: EventTarget): void {

		const src = timer(this.popupDelay).subscribe(() => {

			if (this._cachePackageInfo.has(packageId)) {
				const content = this._cachePackageInfo.get(packageId);
				if (content) {
					this.displayPopupPanel(content, targetElement);
				}
				else {
					// Nothing. No associated content available.
				}
			}
			else {
				this._staticResourceService.getBrandingPackageInfoById(packageId).subscribe({
					next: (htmlContent: string | undefined) => {
						this._cachePackageInfo.set(packageId, htmlContent);
						if (htmlContent) {
							// show as HTML
							this.displayPopupPanel(htmlContent, targetElement);
						}
					},
					error: (error) => {
						// probably the infopanel is not defined for this package
						this._cachePackageInfo.set(packageId, undefined); // set empty content as well: an indication that we tried to load it and should not try again
						this._logger.info(this.COMPONENT_NAME, `Branding infopanel data is requested by user for packageId="${packageId}" but not found on the server`);
					}
				});
			}
		});

		if (this.timerSubs.has(packageId)) {
			// unlikey this can happen, but lets cancel the old timer for this productId
			const oldSub = this.timerSubs.get(packageId);
			this.timerSubs.delete(packageId);
			oldSub?.unsubscribe();
		}

		this.timerSubs.set(packageId, src); // keep the timer's subscription so we can cancel the timer upon mouseleave event
	}

	notifyMouseLeavesPackageLabel(packageId: string): void {

		this.deactivateTimer(packageId);

		if (this._overlayRef) {
			timer(100).subscribe(() => {
				// Close the overlay only if the mouse did not enter the popup panel within the timeout.
				// If the mouse entered the popup panel, it will be closed by its own mouseleave event.
				if (!this._mouseEnteredPopupPanel) {
					this.closeOverlay();
				}
			});
		}
	}

	private displayPopupPanel(htmlContent: string, targetElement: EventTarget): void {
		const target = new ElementRef(targetElement);
		const positionStrategy = this._overlayPositionBuilder
			.flexibleConnectedTo(target)
			.withPositions([
				{
					originX: 'center',
					originY: 'bottom',
					overlayX: 'center',
					overlayY: 'top',
				},
			]);

		this._overlayRef = this._overlay.create({
			positionStrategy: positionStrategy,
			disposeOnNavigation: true // closes the panel when navigation changes while mouse pointer is still inside the panel, for instance, with mouse's Back button
		});

		const componentPortal = new ComponentPortal(PopupPanelComponent); // automatically detaches upon OverlayRef dispose
		const componentRef = this._overlayRef.attach(componentPortal);
		componentRef.instance.customHtmlContent = htmlContent;

		this._mouseEntersSub = componentRef.instance.mouseEnters$.subscribe(() => {
			this._mouseEnteredPopupPanel = true;
		});

		this._mouseLeavesSub = componentRef.instance.mouseLeaves$.subscribe(() => {
			this.closeOverlay();
		});
	}

	private deactivateTimer(packageId: string): void {
		const sub = this.timerSubs.get(packageId);
		if (sub) {
			sub.unsubscribe();
			this.timerSubs.delete(packageId);
		}
	}

	private closeOverlay(): void {
		if (this._overlayRef) {
			this._overlayRef.detach();
			this._overlayRef.dispose();
			this._overlayRef = undefined;
		}

		this._mouseEnteredPopupPanel = false;

		if (this._mouseEntersSub) {
			this._mouseEntersSub.unsubscribe();
			this._mouseEntersSub = undefined;
		}

		if (this._mouseLeavesSub) {
			this._mouseLeavesSub.unsubscribe();
			this._mouseLeavesSub = undefined;
		}
	}
}
